
declare global {
  interface Window {
    WebR: any;
  }
}

export class WebRService {
  private webR: any = null;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private scriptLoadPromise: Promise<void> | null = null;
  private capturedDataFrameNames: Set<string> = new Set();

  private async loadWebR(): Promise<void> {
    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    if (typeof window !== 'undefined' && !window.WebR) {
      this.scriptLoadPromise = (async () => {
        const maxWaitTime = 120000; // 2 minutes
        const checkInterval = 500;
        let elapsedTime = 0;

        while (!window.WebR && elapsedTime < maxWaitTime) {
          await new Promise(resolve => setTimeout(resolve, checkInterval));
          elapsedTime += checkInterval;
        }

        if (!window.WebR) {
          throw new Error('WebR failed to load. Please check your network connection.');
        }
      })();

      return this.scriptLoadPromise;
    }
    return Promise.resolve();
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.doInitialize();
    return this.initializationPromise;
  }

  private async doInitialize(): Promise<void> {
    try {
      // First, ensure the WebR script is loaded
      await this.loadWebR();

      // Check if WebR is available after loading
      if (!window.WebR) {
        throw new Error('WebR is not available');
      }

      console.log('Initializing WebR 0.5.8...');

      // 官方WebR CDN - 最可靠
      // 如果官方CDN慢，可以换成 jsDelivr: https://cdn.jsdelivr.net/npm/webr@0.5.8/dist/
      const baseUrl = 'https://webr.r-wasm.org/v0.5.8/';

      // ChannelType: 0=Automatic, 1=SharedArrayBuffer, 3=PostMessage
      // Using PostMessage (3) for better compatibility
      const ChannelType = { Automatic: 0, SharedBuffer: 1, PostMessage: 3 };

      this.webR = new window.WebR({
        baseUrl: baseUrl,
        channelType: ChannelType.PostMessage,
        showStdout: false,
        showStderr: false,
        RArgs: ['--quiet']
      });

      console.log('WebR object created with PostMessage channel, starting initialization...');
      await this.webR.init();
      console.log('WebR init() completed');

      // 设置基本配置和绘图设备
      try {
        // Set R options for better output formatting
        await this.webR.evalRVoid('options(width = 80, digits = 4)');
        console.log('R options configured');

        // Set up plotting parameters for better plot capture
        await this.webR.evalRVoid('par(mar = c(4, 4, 3, 2), mgp = c(2, 0.8, 0))');
        console.log('R plotting parameters configured');

        // Pre-load ggplot2 for better plot capture
        console.log('Loading ggplot2 package...');
        try {
          await this.webR.evalRVoid('suppressPackageStartupMessages(library(ggplot2))');
          console.log('✓ ggplot2 loaded successfully');

          // Verify ggplot2 is available
          const ggplotVersion = await this.webR.evalR('as.character(packageVersion("ggplot2"))');
          console.log('✓ ggplot2 version:', await ggplotVersion.toJs());
        } catch (ggplotError) {
          console.error('✗ Failed to load ggplot2:', ggplotError);
          throw ggplotError; // Re-throw to indicate initialization failure
        }
      } catch (e) {
        console.warn('Failed to set R options or load ggplot2:', e);
        // Continue even if ggplot2 fails to load - it might not be available
      }

      this.isInitialized = true;
      console.log('WebR 0.5.8 initialized successfully');
    } catch (error) {
      console.error('WebR initialization failed:', error);
      this.webR = null;
      throw error;
    }
  }

  /**
   * Execute selected code - works with persistent R environment
   * State from previous executions is preserved
   */
  async runSelection(code: string): Promise<{ output: string; error: string | null; plots: string[]; dataFrame: any }> {
    // WebR maintains a persistent environment, so runSelection
    // can use the same execution logic as executeCode
    // The only difference is semantic - this is meant for partial code execution
    return this.executeCode(code);
  }

  async executeCode(code: string): Promise<{ output: string; error: string | null; plots: string[]; dataFrame: any }> {
    try {
      await this.initialize();

      if (!this.webR) {
        throw new Error('WebR not available');
      }

      console.log('Executing R code...');

      // WebR 0.5.8 uses a different execution model
      let output = '';
      let error = null;
      let plots: string[] = [];
      let detectedDataFrame: any = null;

      // Detect if code is a View() call or a dataframe variable
      const trimmedCode = code.trim();
      const isViewCall = /^\s*View\s*\(/.test(trimmedCode);

      // If it's a View() call, extract the dataframe name and capture it
      if (isViewCall) {
        const dfMatch = trimmedCode.match(/View\s*\(\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\)/);
        console.log('dfMatch result:', dfMatch);
        if (dfMatch) {
          const dfName = dfMatch[1];
          console.log('View() detected for dataframe:', dfName);

          try {
            // First, check if the dataframe exists
            const existsCheck = await this.webR.evalR(`"${dfName}" %in% ls(.GlobalEnv)`);
            const existsJs = await existsCheck.toJs();
            console.log('Dataframe exists check:', existsJs);

            if (!existsJs.values || !existsJs.values[0]) {
              console.log('Dataframe does not exist in R environment');
            } else {

            // Get dataframe dimensions
            const nrowCapture = await this.webR.evalR(`nrow(get("${dfName}", envir = .GlobalEnv))`);
            const nrowJs = await nrowCapture.toJs();
            const nrow = nrowJs.values[0];

            const ncolCapture = await this.webR.evalR(`ncol(get("${dfName}", envir = .GlobalEnv))`);
            const ncolJs = await ncolCapture.toJs();
            const ncol = ncolJs.values[0];

            console.log('Dataframe dimensions:', { nrow, ncol });

            // Get column names
            const colnamesCapture = await this.webR.evalR(`colnames(get("${dfName}", envir = .GlobalEnv))`);
            const colnamesJs = await colnamesCapture.toJs();
            const colnames = colnamesJs.values || [];

            console.log('Column names:', colnames);

            // Get column types
            const coltypesCapture = await this.webR.evalR(`sapply(get("${dfName}", envir = .GlobalEnv), function(col) class(col)[1])`);
            const coltypesJs = await coltypesCapture.toJs();
            const coltypes = coltypesJs.values || [];

            console.log('Column types:', coltypes);

            // Extract each column's values
            const columns: any[] = [];
            for (let i = 0; i < ncol; i++) {
              const colName = colnames[i];
              const colType = coltypes[i];

              // Get column values as a vector
              const colValuesCapture = await this.webR.evalR(`get("${dfName}", envir = .GlobalEnv)[,${i + 1}]`);
              const colValuesJs = await colValuesCapture.toJs();
              const colValues = colValuesJs.values || [];

              columns.push({
                name: colName,
                type: colType,
                values: colValues
              });
            }

            detectedDataFrame = {
              name: dfName,
              columns: columns,
              rowCount: nrow,
              colCount: ncol
            };

            console.log('View() returning dataframe:', detectedDataFrame);

            return {
              output: '',
              error: null,
              plots: [],
              dataFrame: detectedDataFrame
            };
          }
          } catch (e) {
            console.warn('Failed to capture dataframe from View():', e);
          }
        }
      }

      try {
        // Create a shelter to capture output
        const shelter = await new this.webR.Shelter();

        // Check if code contains ggplot2 calls for special handling
        // More flexible patterns that handle whitespace and different calling styles
        // Remove newlines for regex matching to handle multi-line code
        const codeForRegex = code.replace(/\s+/g, ' ');
        const hasGgplot = /ggplot\s*\(|qplot\s*\(|ggplot2::\s*(ggplot|qplot)\s*\(/.test(codeForRegex);
        const hasBasePlot = /plot\s*\(|hist\s*\(|boxplot\s*\(|barplot\s*\(|pairs\s*\(|matplot\s*\(/.test(codeForRegex);

        console.log('Checking for plotting code...');
        console.log('  hasGgplot:', hasGgplot);
        console.log('  hasBasePlot:', hasBasePlot);
        console.log('  Original code length:', code.length);
        console.log('  Code preview:', code.substring(0, 200));

        // Prepare the code to execute
        let codeToExecute = code;

        // For plotting code, we need to ensure the canvas device is active
        // and plots are properly captured
        if (hasGgplot || hasBasePlot) {
          // For ggplot2, we need to explicitly print the plot object to render it
          // For base R plots, they render automatically but we need to capture them
          if (hasGgplot) {
            // Check if the code contains an assignment operator (<- or =)
            // If not, we need to wrap it in print() to render the plot
            const hasAssignment = /<-\s*[^=]|=\s*ggplot/.test(code);

            if (!hasAssignment) {
              // Direct ggplot expression without assignment - wrap in print()
              // Indent all lines properly
              const indentedCode = code.split('\n').map(line => '  ' + line).join('\n');
              codeToExecute = `print({\n${indentedCode}\n})`;
            } else {
              // Has assignment - execute normally, then print the assigned variable
              codeToExecute = code + `\n\n# Print the plot if a ggplot object was created\nif (exists(".Last.value", envir = .GlobalEnv)) {\n  obj <- get(".Last.value", envir = .GlobalEnv)\n  if (inherits(obj, c("gg", "ggplot", "ggplot_built"))) {\n    print(obj)\n  }\n}`;
            }
          }
        }

        // Execute the code and capture stdout/stderr
        const result = await shelter.captureR(codeToExecute);

        // WebR 0.5.8: Check if we got any images in the result
        // In WebR 0.5.8, plots are captured in result.images as ImageBitmap objects
        if (result.images && Array.isArray(result.images) && result.images.length > 0) {
          console.log(`✓ Captured ${result.images.length} plot(s)`);

          // Convert all ImageBitmaps to base64 data URLs
          const plotPromises = result.images.map(async (imageBitmap: ImageBitmap, index: number) => {
            console.log(`Processing plot ${index}:`, {
              type: imageBitmap.constructor.name,
              width: imageBitmap.width,
              height: imageBitmap.height
            });

            try {
              // Create a canvas to convert ImageBitmap to base64
              const canvas = document.createElement('canvas');
              canvas.width = imageBitmap.width;
              canvas.height = imageBitmap.height;
              const ctx = canvas.getContext('2d');

              if (ctx) {
                // Draw the ImageBitmap onto the canvas
                ctx.drawImage(imageBitmap, 0, 0);

                // Convert to base64 PNG
                const dataUrl = canvas.toDataURL('image/png');
                console.log(`  ✓ Converted plot ${index} to data URL (${dataUrl.length} chars)`);

                // Close the ImageBitmap to free memory
                imageBitmap.close();

                return dataUrl;
              } else {
                console.error(`  ✗ Failed to get canvas context for plot ${index}`);
                imageBitmap.close();
                return null;
              }
            } catch (err) {
              console.error(`  ✗ Error processing plot ${index}:`, err);
              imageBitmap.close();
              return null;
            }
          });

          // Wait for all plots to be converted
          const convertedPlots = await Promise.all(plotPromises);
          plots = convertedPlots.filter((p): p is string => p !== null);
          console.log(`✓ Successfully converted ${plots.length}/${result.images.length} plot(s) to base64`);
        } else {
          console.log('✗ No plots found in result.images');
          console.log('   result.images is:', result.images);

          // Check if code contained plotting functions
          const hasPlotting = /plot\s*\(|hist\s*\(|boxplot\s*\(|barplot\s*\(|scatter\.plot\s*\(|ggplot\s*\(|qplot\s*\(|pairs\s*\(|matplot\s*\(/.test(code);
          if (hasPlotting) {
            console.warn('⚠ Plotting functions detected but no plots were captured!');

            // Special handling for ggplot2: check if result is a ggplot object and print it
            if (hasGgplot) {
              console.log('📊 ggplot2 detected - attempting to capture plot');

              try {
                // Try to find and print the ggplot object
                // First, check if the last value is a ggplot
                const printShelter = await new this.webR.Shelter();
                const printResult = await printShelter.captureR(`
                  tryCatch({
                    # Get all objects in global environment
                    objs <- ls(.GlobalEnv)
                    # Find ggplot objects
                    ggplots <- objs[sapply(objs, function(name) {
                      obj <- get(name, envir = .GlobalEnv)
                      inherits(obj, "gg") || inherits(obj, "ggplot")
                    })]
                    if (length(ggplots) > 0) {
                      # Print the most recently created ggplot
                      print(get(ggplots[length(ggplots)], envir = .GlobalEnv))
                    } else {
                      # Try .Last.value
                      if (exists(".Last.value", envir = .GlobalEnv)) {
                        obj <- get(".Last.value", envir = .GlobalEnv)
                        if (inherits(obj, "gg") || inherits(obj, "ggplot")) {
                          print(obj)
                        }
                      }
                    }
                  }, error = function(e) NULL)
                `);

                console.log('ggplot print result.images:', printResult.images);

                if (printResult.images && printResult.images.length > 0) {
                  console.log(`✓ Successfully captured ${printResult.images.length} ggplot(s)`);

                  // Convert the newly captured plots
                  const plotPromises = printResult.images.map(async (imageBitmap: ImageBitmap, index: number) => {
                    try {
                      const canvas = document.createElement('canvas');
                      canvas.width = imageBitmap.width;
                      canvas.height = imageBitmap.height;
                      const ctx = canvas.getContext('2d');

                      if (ctx) {
                        ctx.drawImage(imageBitmap, 0, 0);
                        const dataUrl = canvas.toDataURL('image/png');
                        imageBitmap.close();
                        return dataUrl;
                      } else {
                        imageBitmap.close();
                        return null;
                      }
                    } catch (err) {
                      console.error(`Error processing plot ${index}:`, err);
                      imageBitmap.close();
                      return null;
                    }
                  });

                  const convertedPlots = await Promise.all(plotPromises);
                  plots = convertedPlots.filter((p): p is string => p !== null);
                  console.log(`✓ Total ggplot plots captured: ${plots.length}`);
                } else {
                  console.warn('✗ No ggplot plots captured - ggplot2 might not be properly loaded or device not initialized');
                }

                await printShelter.purge();
              } catch (e) {
                console.warn('Failed to capture ggplot plot:', e);
              }
            } else {
              console.warn('  This might indicate:');
              console.warn('  1. WebR plot device is not properly initialized');
              console.warn('  2. The plotting functions were called but produced no output');
              console.warn('  3. There is a compatibility issue with the WebR version');
            }
          }
        }

        // Process the output array - filter and format messages
        if (result.output && result.output.length > 0) {
          const outputLines: string[] = [];

          for (const msg of result.output) {
            // Handle different output types from WebR
            if (msg.type === 'stdout') {
              const data = String(msg.data);

              // Skip empty lines
              if (!data.trim()) {
                continue;
              }

              // Filter out plot-related numeric output
              // R plotting functions print the data vector with index notation like:
              // [1] 23 45 67 89 12 34 56 78 90 43
              const hasPlots = result.images && result.images.length > 0;

              if (hasPlots) {
                // Skip R vector notation - lines starting with [number]
                // This is the pattern R uses when printing vectors: [1] 10 20 30
                if (/^\[\d+\]\s/.test(data)) {
                  console.log('Filtering out R vector output:', data);
                  continue;
                }
              }

              outputLines.push(data);
            } else if (msg.type === 'stderr') {
              outputLines.push(`Error: ${msg.data}`);
            }
          }

          output = outputLines.join('\n');
        }

        // Also include the result value if available
        if (result.result) {
          try {
            const resultJs = await result.result.toJs();
            if (resultJs && resultJs.values && resultJs.values.length > 0) {
              const resultText = resultJs.values
                .map((v: any) => this.formatRValue(v))
                .join('\n');
              if (resultText && resultText !== 'NULL') {
                output += (output ? '\n' : '') + resultText;
              }
            }
          } catch (e) {
            // Ignore conversion errors for the result
          }
        }

        // Clean up the shelter
        await shelter.purge();

        // Auto-detect and capture only newly created dataframes in the global environment
        let detectedDataFrame: any = null;
        try {
          console.log('🔍 Scanning for newly created dataframes...');
          console.log(`   Already captured: [${Array.from(this.capturedDataFrameNames).join(', ')}]`);

          // Get all objects in the global environment
          const objNamesResult = await this.webR.evalR(`
            tryCatch({
              objs <- ls(.GlobalEnv)
              # Filter for dataframes and matrices only
              dataframe_names <- objs[sapply(objs, function(name) {
                obj <- get(name, envir = .GlobalEnv)
                is.data.frame(obj) || is.matrix(obj)
              })]
              as.character(dataframe_names)
            }, error = function(e) character(0))
          `);

          const objNamesJs = await objNamesResult.toJs();
          const dataframeNames = objNamesJs.values || [];

          console.log(`Found ${dataframeNames.length} dataframe(s)/matrix(s) in environment:`, dataframeNames);

          // Filter out already captured dataframes
          const newDataframeNames = dataframeNames.filter((name: string) => !this.capturedDataFrameNames.has(name));

          if (newDataframeNames.length > 0) {
            console.log(`New dataframes to capture:`, newDataframeNames);

            // Capture the most recently created new dataframe
            const lastDfName = newDataframeNames[newDataframeNames.length - 1];
            console.log(`Capturing new dataframe: ${lastDfName}`);

            // Get dataframe dimensions
            const nrowCapture = await this.webR.evalR(`nrow(get("${lastDfName}", envir = .GlobalEnv))`);
            const nrowJs = await nrowCapture.toJs();
            const nrow = nrowJs.values[0];

            const ncolCapture = await this.webR.evalR(`ncol(get("${lastDfName}", envir = .GlobalEnv))`);
            const ncolJs = await ncolCapture.toJs();
            const ncol = ncolJs.values[0];

            console.log(`Dataframe dimensions: ${nrow} rows × ${ncol} cols`);

            // Get column names
            const colnamesCapture = await this.webR.evalR(`colnames(get("${lastDfName}", envir = .GlobalEnv))`);
            const colnamesJs = await colnamesCapture.toJs();
            const colnames = colnamesJs.values || [];

            console.log('Column names:', colnames);

            // Get column types
            const coltypesCapture = await this.webR.evalR(`sapply(get("${lastDfName}", envir = .GlobalEnv), function(col) class(col)[1])`);
            const coltypesJs = await coltypesCapture.toJs();
            const coltypes = coltypesJs.values || [];

            console.log('Column types:', coltypes);

            // Extract each column's values
            const columns: any[] = [];
            for (let i = 0; i < ncol; i++) {
              const colName = colnames[i];
              const colType = coltypes[i];

              // Get column values as a vector
              const colValuesCapture = await this.webR.evalR(`get("${lastDfName}", envir = .GlobalEnv)[,${i + 1}]`);
              const colValuesJs = await colValuesCapture.toJs();
              const colValues = colValuesJs.values || [];

              columns.push({
                name: colName,
                type: colType,
                values: colValues
              });
            }

            detectedDataFrame = {
              name: lastDfName,
              columns: columns,
              rowCount: nrow,
              colCount: ncol
            };

            // Mark this dataframe as captured
            this.capturedDataFrameNames.add(lastDfName);
            console.log(`✓ Successfully captured dataframe: ${lastDfName}`);
            console.log(`   Updated captured list: [${Array.from(this.capturedDataFrameNames).join(', ')}]`);
          } else {
            console.log('No new dataframes found (all existing dataframes already captured)');
          }
        } catch (e) {
          console.warn('Failed to auto-detect dataframes:', e);
        }

        if (detectedDataFrame) {
          return {
            output,
            error: null,
            plots: plots.length > 0 ? plots : [],
            dataFrame: detectedDataFrame
          };
        }

        // If no output from captureR, try evaluating as expression
        // But make sure we don't lose any plots that were captured
        if (!output && plots.length === 0) {
          try {
            const evalResult = await this.webR.evalR(code);
            const jsResult = await evalResult.toJs();

            if (jsResult.values && jsResult.values.length > 0) {
              output = jsResult.values.map((v: any) => this.formatRValue(v)).join('\n');
            }

            await evalResult.destroy();
          } catch (e) {
            // Evaluation failed, but code might have succeeded
            // Return empty output instead of message
          }
        }

        // Return empty output if nothing meaningful to show
        if (!output) {
          output = '';
        }

      } catch (err) {
        console.error('R execution error:', err);
        error = err instanceof Error ? err.message : 'R code execution failed';
        output = '';
        plots = [];
      }

      return {
        output,
        error,
        plots: plots || [],
        dataFrame: null
      };

    } catch (err) {
      console.error('WebR execution error:', err);
      return {
        output: '',
        error: err instanceof Error ? err.message : 'Unknown error occurred',
        plots: [],
        dataFrame: null
      };
    }
  }

  async interrupt(): Promise<void> {
    try {
      if (this.webR) {
        await this.webR.interrupt();
      }
    } catch (error) {
      console.error('Error interrupting R execution:', error);
    }
  }

  async getCompletions(code: string, position: number): Promise<string[]> {
    try {
      await this.initialize();

      if (!this.webR) {
        return [];
      }

      // 简单的补全逻辑（可以扩展）
      const commonRFunctions = [
        'mean', 'median', 'sd', 'var', 'sum', 'max', 'min', 'range',
        'length', 'str', 'summary', 'head', 'tail', 'plot', 'hist',
        'boxplot', 'abline', 'lm', 'glm', 't.test', 'cor', 'aov',
        'anova', 'library', 'require', 'install.packages', 'source',
        'read.csv', 'write.csv', 'data.frame', 'c', 'list', 'matrix',
        'array', 'factor', 'as.numeric', 'as.character', 'as.factor'
      ];

      // 获取当前单词
      const beforeCursor = code.substring(0, position);
      const words = beforeCursor.match(/(\w+)$/);
      const currentWord = words ? words[1] : '';

      if (!currentWord) return [];

      return commonRFunctions.filter(func =>
        func.toLowerCase().startsWith(currentWord.toLowerCase())
      );
    } catch (error) {
      console.error('Error getting completions:', error);
      return [];
    }
  }

  isReady(): boolean {
    return this.isInitialized && this.webR !== null;
  }

  /**
   * Reset the captured dataframes tracking
   * Call this when starting a fresh code execution
   */
  resetCapturedDataFrames(): void {
    this.capturedDataFrameNames.clear();
    console.log('🔄 Captured dataframes list cleared');
  }

  /**
   * Format R value for display
   * Converts WebR objects to readable string representations
   */
  private formatRValue(value: any, depth: number = 0): string {
    const MAX_DEPTH = 5;
    const MAX_ARRAY_LENGTH = 50;

    if (depth > MAX_DEPTH) {
      return '[... nested too deep ...]';
    }

    // Handle null/undefined
    if (value === null || value === undefined) {
      return 'NULL';
    }

    // Handle NaN (R's NA)
    if (typeof value === 'number' && isNaN(value)) {
      return 'NA';
    }

    // Handle primitive types
    if (typeof value === 'boolean') {
      return value ? 'TRUE' : 'FALSE';
    }

    if (typeof value === 'number') {
      // Format numbers nicely
      if (Number.isInteger(value)) {
        return value.toString();
      }
      return value.toFixed(4).replace(/\.?0+$/, '');
    }

    if (typeof value === 'string') {
      return value;
    }

    // Handle arrays/vectors
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return 'NULL';
      }

      // Check if all elements are NA
      const allNA = value.every((v: any) =>
        v === null || v === undefined || (typeof v === 'number' && isNaN(v))
      );
      if (allNA) {
        return 'NA';
      }

      // Limit array output for large arrays
      const displayValue = value.length > MAX_ARRAY_LENGTH
        ? value.slice(0, MAX_ARRAY_LENGTH)
        : value;

      const formatted = displayValue
        .map((v: any) => this.formatRValue(v, depth + 1))
        .join(' ');

      if (value.length > MAX_ARRAY_LENGTH) {
        return formatted + ' ... (truncated)';
      }
      return formatted;
    }

    // Handle objects with type property (R objects)
    if (value.type && value.values) {
      const type = value.type;

      if (type === 'null') {
        return 'NULL';
      }

      if (type === 'logical') {
        return value.values
          .map((v: any) => v === null ? 'NA' : (v ? 'TRUE' : 'FALSE'))
          .join(' ');
      }

      if (type === 'integer' || type === 'double') {
        return value.values
          .map((v: any) => v === null ? 'NA' : this.formatRValue(v, depth + 1))
          .join(' ');
      }

      if (type === 'character') {
        return value.values
          .map((v: any) => v === null ? 'NA' : `"${v}"`)
          .join(' ');
      }

      if (type === 'list') {
        if (value.names && value.names.length > 0) {
          return value.names.map((name: string, i: number) => {
            const val = value.values[i];
            return `$${name}\n${this.formatRValue(val, depth + 1).split('\n').map((l: string) => '  ' + l).join('\n')}`;
          }).join('\n');
        }
        return '[[list]]';
      }

      // Generic object representation
      if (value.values && value.values.length > 0) {
        return value.values
          .map((v: any) => this.formatRValue(v, depth + 1))
          .join(' ');
      }

      return type;
    }

    // Handle plain objects
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return 'NULL';
      }

      return keys.map(key => {
        const val = (value as any)[key];
        return `${key}: ${this.formatRValue(val, depth + 1)}`;
      }).join('\n');
    }

    return String(value);
  }

  async cleanup(): Promise<void> {
    try {
      if (this.webR) {
        await this.webR.close();
        this.webR = null;
      }
      this.isInitialized = false;
      this.initializationPromise = null;
      this.scriptLoadPromise = null;
    } catch (error) {
      console.error('Error cleaning up WebR:', error);
    }
  }
}

// 创建单例实例
export const webrService = new WebRService();